/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  ViewChild,
} from "@angular/core";

import {ColDef} from "@ag-grid-community/core";
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {MatTableDataSource} from "@angular/material/table";
import {CommonState} from "@app/store/common/common.state";
import {AlertController, ModalController, ToastController} from "@ionic/angular";
import {Store} from "@ngxs/store";
import {GroupMultiPatient, QuestionnairesService, VideoChatService} from "@trec/api";
import {ModalAddMultiPatientQuestionnairesComponent} from "./modals/modal-add-multi-patient-questionnaires/modal-add-multi-patient-questionnaires.component";
import {ModalAddMultiPatientComponent} from "./modals/modal-add-multi-patient/modal-add-multi-patient.component";
import {ModalDetailMultiPatientComponent} from "./modals/modal-detail-multi-patient/modal-detail-multi-patient.component";
@Component({
  selector: "c3-patient-group-page",
  templateUrl: "./patient-group-page.component.html",
  styleUrls: ["./patient-group-page.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PatientGroupPageComponent implements OnInit, AfterViewInit {
  groupsList: GroupMultiPatient[] = [];
  groupSurveyList: any[] = [];
  trecUserId;
  patientId;
  teleconsultoEnabled = false;
  surveyEnabled = false;
  structureId = localStorage.getItem("c3-curr-structure");
  // Columns for the main (all groups) table
  // Columns for the main (all groups) table
  displayedColumnsMain: string[] = [
    "data_type",
    "start_date",
    "patientsNumbers",
    "spacer",
    "actions",
  ];
  // Columns for teleconsulto table (includes participants count)
  displayedTeleconsultoColumns: string[] = [
    "data_type",
    "start_date",
    "patientsNumbers",
    "spacer",
    "actions",
  ];
  // Columns for survey table
  displayedSurveyColumns: string[] = [
    "data_type",
    "start_date",
    "patientsNumbers",
    "spacer",
    "actions",
  ];
  televisitColumnDefs: ColDef[] = [];
  surveyColumnDefs: ColDef[] = [];
  defaultColDef: ColDef | any = {sortable: true, filter: true, resizable: true};
  dataSource = new MatTableDataSource<GroupMultiPatient>([]);
  // Separate data sources per table so each paginator works independently
  dataSourceTele = new MatTableDataSource<GroupMultiPatient>([]);
  dataSourceSurvey = new MatTableDataSource<any>([]);

  @ViewChild("paginatorMain") paginatorMain: MatPaginator | null = null;
  @ViewChild("paginatorTele") paginatorTele: MatPaginator | null = null;
  @ViewChild("paginatorSurvey") paginatorSurvey: MatPaginator | null = null;
  @ViewChild("sortTele") sortTele: MatSort | null = null;
  @ViewChild("sortSurvey") sortSurvey: MatSort | null = null;

  constructor(
    private modalController: ModalController,
    private cd: ChangeDetectorRef,
    private store: Store,
    private videochatService: VideoChatService,
    private questionnairesService: QuestionnairesService,
    private toastController: ToastController,
    private alertCtrl: AlertController,
  ) { }
  //private modalController: ModalController,

  ngOnInit() {
    this.patientId = this.store.selectSnapshot(CommonState.patientId);
    this.trecUserId = this.store.selectSnapshot(CommonState.userId);
    const configuratoreTelevisita = localStorage.getItem("configuratoreTelevisita");
    const configuratoreSurvey = localStorage.getItem("survey");
    this.teleconsultoEnabled =
      configuratoreTelevisita === "true" || configuratoreTelevisita === "1";
    this.surveyEnabled = configuratoreSurvey === "true" || configuratoreSurvey === "1";
    if (this.teleconsultoEnabled) {
      this.getGroupsList();
    }
    if (this.surveyEnabled) {
      this.getSurveyList();
    }
  }

  ngAfterViewInit(): void {
    // attach paginators when view initialized

    if (this.paginatorTele) {
      this.dataSourceTele.paginator = this.paginatorTele;
    }
    if (this.paginatorSurvey) {
      this.dataSourceSurvey.paginator = this.paginatorSurvey;
    }
    // configure sorting data accessor and attach MatSort
    this.dataSourceTele.sortingDataAccessor = (item: any, prop: string) => {
      if (prop === "data_type") return (item.groupName ?? item.name ?? "").toString().toLowerCase();
      if (prop === "start_date")
        return new Date(item.createdDate ?? item.createDate ?? 0).getTime();
      if (prop === "patientsNumbers")
        return item.participantsGroup?.length ?? item.users?.length ?? 0;
      return (item[prop] ?? "").toString();
    };
    this.dataSourceSurvey.sortingDataAccessor = (item: any, prop: string) => {
      if (prop === "data_type") return (item.name ?? item.groupName ?? "").toString().toLowerCase();
      if (prop === "start_date")
        return new Date(item.createDate ?? item.createdDate ?? 0).getTime();
      if (prop === "patientsNumbers")
        return item.users?.length ?? item.participantsGroup?.length ?? 0;
      return (item[prop] ?? "").toString();
    };

    if (this.sortTele) {
      this.dataSourceTele.sort = this.sortTele;
    }
    if (this.sortSurvey) {
      this.dataSourceSurvey.sort = this.sortSurvey;
    }
  }

  trackById(index: number, item: any) {
    return item?.id ?? index;
  }

  async openDetail(item, mode: "televisita" | "survey" | "default" = "default") {
    //console.log(item);
    const modal = await this.modalController.create({
      component: ModalDetailMultiPatientComponent,
      componentProps: {
        id: item.id,
        name: mode === "televisita" ? item.groupName : item.name,
        physician: item.physician,
        multiPatientList: mode === "televisita" ? item.participantsGroup : item.users,
        mode: mode,
      },
      cssClass: "c3-modal-width-md c3-modal-height-lg",
    });
    modal.onDidDismiss().then((data) => {
      this.getGroupsList();
      this.getSurveyList();
      this.cd.markForCheck();
    });
    return await modal.present();
  }

  async delete(item, mode: "televisita" | "survey" | "default" = "default") {
    const alert = await this.alertCtrl.create({
      header: "Cancellazione gruppo",
      message: `Vuoi cancellare questo gruppo multipaziente?`,
      buttons: [
        {text: "No", role: "cancel"},
        {
          text: "Sì",
          handler: () => {
            try {
              if (mode === "televisita" || mode === "default") {
                this.videochatService.deleteMultiPatient(item.id).subscribe(
                  (data) => {
                    this.successToast();
                    this.getGroupsList();
                  },
                  (err) => {
                    this.errorToast(err._message || "Errore durante la cancellazione del gruppo.");
                  },
                );
              }

              if (mode === "survey" || mode === "default") {
                //aggiungere servizio cancellazione survey
                this.questionnairesService.deleteGroup(item.id).subscribe(
                  (data) => {
                    this.successToast();
                    this.getSurveyList();
                  },
                  (err) => {
                    this.errorToast(err._message || "Errore durante la cancellazione del gruppo.");
                  },
                );
              }
            } catch (e) {
              console.error(e);
            }
          },
        },
      ],
    });

    await alert.present();
  }

  async getGroupsList() {
    this.videochatService.getMultiPatient().subscribe(
      (data) => {
        this.groupsList = data;
        this.dataSourceTele.data = data;
        this.cd.markForCheck();
      },
      (err) => {
        this.groupsList = [];
        this.dataSourceTele.data = [];
        this.cd.markForCheck();
      },
    );
  }

  async getSurveyList() {
    this.questionnairesService.getGroups(this.structureId!).subscribe(
      (data) => {
        this.groupSurveyList = data ?? [];
        this.dataSourceSurvey.data = data ?? [];
        this.cd.markForCheck();
      },
      (err) => {
        console.error(err);
        this.groupSurveyList = [];
        this.dataSourceSurvey.data = [];
        this.cd.markForCheck();
      },
    );
  }

  async addGroup(mode: "televisita" | "default" = "default") {
    const modal = await this.modalController.create({
      component: ModalAddMultiPatientComponent,
      componentProps: {mode},
      cssClass: "c3-modal-width-md c3-modal-height-lg",
    });

    modal.onDidDismiss().then((data) => {
      if (mode === "televisita") {
        this.getGroupsList();
      }
    });
    return await modal.present();
  }
  async addQuestionnaireGroup(mode: "survey" | "default" = "default") {
    const modal = await this.modalController.create({
      component: ModalAddMultiPatientQuestionnairesComponent,
      componentProps: {mode},
      cssClass: "c3-modal-width-md c3-modal-height-lg",
    });

    modal.onDidDismiss().then((data) => {
      if (mode === "survey") {
        this.getSurveyList();
      }
    });
    return await modal.present();
  }

  async successToast() {
    const toast = await this.toastController.create({
      header: "Successo",
      message: "Operazione completata con successo.",
      duration: 3000,
      color: "success",
      position: "bottom",
    });
    toast.present();
    await this.modalController.dismiss();
  }

  async errorToast(err: any) {
    const toast = await this.toastController.create({
      header: "Errore",
      message: err,
      duration: 3000,
      color: "danger",
      position: "bottom",
    });
    toast.present();
    await this.modalController.dismiss();
  }
}
